import { useState, useEffect } from 'react';
import { Grid, Users, ChevronsLeft, User, Clock, Award, Plus, AlertCircle, Loader, ChevronDown, ChevronUp, Timer, TrendingUp, Zap, List, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";

const LightMiniKeysCluster = ({ onBack }) => {
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(false);
    const [queueData, setQueueData] = useState([]);
    const [userPlaces, setUserPlaces] = useState([]);
    const [pendingPlaces, setPendingPlaces] = useState([]);
    const [fullPendingQueue, setFullPendingQueue] = useState([]);
    const [fullMainQueue, setFullMainQueue] = useState([]);
    const [expandedCards, setExpandedCards] = useState(new Set());
    const [showFullQueue, setShowFullQueue] = useState(false);
    const [showMainQueue, setShowMainQueue] = useState(false);
    const [statistics, setStatistics] = useState({
        total_in_queue: 0,
        user_places: 0,
        completed_places: 0,
        earned: 0,
        reserve_fund: 0,
        estimated_time_to_payment: 'Загрузка...'
    });
    const navigate = useNavigate();

    const [canPurchase, setCanPurchase] = useState(true);
    const [purchaseInfo, setPurchaseInfo] = useState(null);

    // STATE ДЛЯ ТАЙМЕРА И СТАТУСОВ
    const [purchaseCountdown, setPurchaseCountdown] = useState(0);
    const [nextPurchaseTime, setNextPurchaseTime] = useState(null);
    const [purchaseStatus, setPurchaseStatus] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    // Состояние для ручной проверки
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [processingTime, setProcessingTime] = useState(0);
    const [notification, setNotification] = useState(null);

    // Состояние "Я готов участвовать"
    const [isReady, setIsReady] = useState(false);
    const [totalReady, setTotalReady] = useState(0);
    const [markingReady, setMarkingReady] = useState(false);

    // ============================================
    // API BASE URL
    // ============================================
    const API_BASE = 'https://api.light-platform.com/api/v1/light-keys-mini';

    const getAuthHeaders = () => {
        const token = localStorage.getItem('auth_token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    // ============================================
    // ЦВЕТА MINI KEYS (зелёная тема)
    // ============================================
    const THEME = {
        primary: 'emerald',
        gradient: 'from-emerald-500 to-teal-600',
        bg: 'bg-emerald-500/5',
        text: 'text-emerald-500',
        border: 'border-emerald-500/20',
        hover: 'hover:bg-emerald-500/10',
        ring: 'ring-emerald-500/30',
    };

    // Форматирование даты с микросекундами
    const formatDateWithMicroseconds = (dateString) => {
        if (!dateString) return 'Н/Д';
        if (dateString.includes('.') && dateString.split('.').length === 4) {
            return dateString;
        }
        const parts = dateString.split(' ');
        if (parts.length === 2) {
            const [datePart, timePart] = parts;
            const [year, month, day] = datePart.split('-');
            const [time, microseconds] = timePart.split('.');
            return `${day}.${month}.${year} ${time}.${microseconds || '000000'}`;
        }
        return dateString;
    };

    const MicrosecondsDisplay = ({ dateString, short = false }) => {
        const formatted = formatDateWithMicroseconds(dateString);
        const parts = formatted.split('.');

        if (parts.length === 4) {
            const microseconds = parts[3];
            if (short) {
                return (
                    <div className="text-[10px] leading-tight">
                        <div className="font-mono text-gray-600">{parts[0]}.{parts[1]}.{parts[2]}</div>
                        <div className="font-mono text-emerald-500 font-semibold">{parts[3].substring(0, 2)}:{parts[3].substring(2, 4)} .{microseconds}</div>
                    </div>
                );
            }
            return (
                <span className="font-mono text-xs sm:text-sm">
                    {parts[0]}.{parts[1]}.{parts[2]} {parts[3].substring(0, 2)}:{parts[3].substring(2, 4)}:{parts[3].substring(4, 6)}<span className="text-emerald-500 font-semibold">.{microseconds}</span>
                </span>
            );
        }
        return <span className="font-mono text-xs sm:text-sm">{formatted}</span>;
    };

    // Форматирование таймера
    const formatCountdown = (seconds) => {
        if (seconds <= 0) return { days: 0, hours: '00', mins: '00', secs: '00' };

        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        return {
            days,
            hours: hours.toString().padStart(2, '0'),
            mins: mins.toString().padStart(2, '0'),
            secs: secs.toString().padStart(2, '0')
        };
    };

    const countdown = formatCountdown(purchaseCountdown);

    // Таймер обработки покупки
    useEffect(() => {
        if (!isProcessing || !purchaseStatus?.clicked_at) {
            setProcessingTime(0);
            return;
        }

        const startTime = new Date(purchaseStatus.clicked_at).getTime();
        const interval = setInterval(() => {
            const now = Date.now();
            setProcessingTime(Math.floor((now - startTime) / 1000));
        }, 1000);

        return () => clearInterval(interval);
    }, [isProcessing, purchaseStatus?.clicked_at]);

    // Обратный отсчет
    useEffect(() => {
        if (purchaseCountdown <= 0) return;

        const interval = setInterval(() => {
            setPurchaseCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(interval);
                    // Лёгкая проверка доступности покупки (без перезагрузки страницы)
                    checkPurchaseAvailability();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [purchaseCountdown]);

    // Лёгкая проверка доступности покупки
    const checkPurchaseAvailability = async () => {
        try {
            const response = await axios.get(`${API_BASE}/can-purchase`, {
                headers: getAuthHeaders()
            });
            if (response.data?.success) {
                const data = response.data.data;
                setCanPurchase(data.can_purchase);
                setPurchaseInfo(data);
                if (data.seconds_until_available > 0) {
                    setPurchaseCountdown(data.seconds_until_available);
                } else {
                    setPurchaseCountdown(0);
                }
            }
        } catch (error) {
            console.error('Error checking purchase availability:', error);
        }
    };

    // ============================================
    // ЗАГРУЗКА ДАННЫХ
    // ============================================

    const fetchData = async (showLoader = true) => {
        try {
            if (showLoader) setLoading(true);

            const [queueResponse, canPurchaseResponse, purchaseStatusResponse] = await Promise.all([
                axios.get(`${API_BASE}/queue-data`, { headers: getAuthHeaders() }),
                axios.get(`${API_BASE}/can-purchase`, { headers: getAuthHeaders() }).catch(() => null),
                axios.get(`${API_BASE}/purchase-status`, { headers: getAuthHeaders() }).catch(() => null)
            ]);

            // Загружаем счётчик готовых участников
            try {
                const readyResponse = await axios.get(`${API_BASE}/ready-count`, { headers: getAuthHeaders() });
                if (readyResponse.data?.success) {
                    setTotalReady(readyResponse.data.data.total_ready || 0);
                    setIsReady(readyResponse.data.data.is_ready || false);
                }
            } catch (e) {
                // Игнорируем — таблица может не существовать
            }

            if (queueResponse.data.success) {
                const data = queueResponse.data.data;
                setQueueData(data.queue || []);
                setUserPlaces(data.user_places || []);
                setPendingPlaces(data.pending_places || []);
                setFullPendingQueue(data.full_pending_queue || []);
                setFullMainQueue(data.full_main_queue || []);
                setStatistics(data.statistics || {});
            }

            if (canPurchaseResponse?.data?.success) {
                const purchaseData = canPurchaseResponse.data.data;
                setCanPurchase(purchaseData.can_purchase);
                setPurchaseInfo(purchaseData);

                if (purchaseData.seconds_until_available > 0) {
                    setPurchaseCountdown(purchaseData.seconds_until_available);
                } else {
                    setPurchaseCountdown(0);
                }

                // Если есть необработанный клик — показываем статус обработки
                if (purchaseData.reason === 'has_pending_click') {
                    setIsProcessing(true);
                    setPurchaseStatus({
                        clicked_at: purchaseData.clicked_at,
                        status: 'pending'
                    });
                }
            }

            // Проверяем статус покупки отдельно (для восстановления после перезагрузки)
            if (purchaseStatusResponse?.data?.success && purchaseStatusResponse.data.data?.has_click) {
                const statusData = purchaseStatusResponse.data.data;
                if (statusData.status === 'pending') {
                    setIsProcessing(true);
                    setPurchaseStatus(statusData);
                }
            }

        } catch (error) {
            console.error('Error fetching Mini Keys data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // ============================================
    // ГОТОВНОСТЬ К УЧАСТИЮ
    // ============================================

    const handleMarkReady = async () => {
        if (markingReady || isReady) return;

        try {
            setMarkingReady(true);
            const response = await axios.post(`${API_BASE}/mark-ready`, {}, {
                headers: getAuthHeaders()
            });

            if (response.data.success) {
                setIsReady(true);
                if (response.data.data?.total_ready) {
                    setTotalReady(response.data.data.total_ready);
                }
                setNotification({ type: 'success', message: response.data.message });
            } else {
                setNotification({ type: 'error', message: response.data.message });
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Ошибка при отметке готовности';
            setNotification({ type: 'error', message });
        } finally {
            setMarkingReady(false);
        }
    };

    // ============================================
    // ПОКУПКА
    // ============================================

    const handlePurchase = async () => {
        if (purchasing || !canPurchase || isProcessing) return;

        try {
            setPurchasing(true);

            const response = await axios.post(`${API_BASE}/purchase`, {}, {
                headers: getAuthHeaders()
            });

            if (response.data.success) {
                setNotification({ type: 'success', message: response.data.message });
                setPurchaseStatus(response.data.data);
                setIsProcessing(true);

                // Запускаем проверку статуса
                startPurchaseStatusPolling();
            } else {
                setNotification({ type: 'error', message: response.data.message });
            }

        } catch (error) {
            const message = error.response?.data?.message || 'Ошибка при покупке';
            setNotification({ type: 'error', message });
        } finally {
            setPurchasing(false);
        }
    };

    // Поллинг статуса покупки
    const startPurchaseStatusPolling = () => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await axios.get(`${API_BASE}/purchase-status`, {
                    headers: getAuthHeaders()
                });

                if (response.data.success) {
                    const data = response.data.data;

                    if (!data.has_click || data.status === 'processed') {
                        clearInterval(pollInterval);
                        setIsProcessing(false);
                        setPurchaseStatus(null);
                        setNotification({ type: 'success', message: 'Покупка успешно обработана!' });
                        fetchData(false);
                    } else if (data.status === 'failed') {
                        clearInterval(pollInterval);
                        setIsProcessing(false);
                        setNotification({ type: 'error', message: data.error_message || 'Ошибка обработки покупки' });
                    }
                }
            } catch (error) {
                console.error('Error polling purchase status:', error);
            }
        }, 5000);

        // Останавливаем через 5 минут
        setTimeout(() => {
            clearInterval(pollInterval);
            setIsProcessing(false);
        }, 300000);
    };

    // Скрытие уведомлений
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    // ============================================
    // СТАТУС БАРЫ
    // ============================================

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'bg-emerald-500';
            case 'next-to-pay': return 'bg-yellow-500';
            case 'waiting-payment': return 'bg-blue-500';
            default: return 'bg-gray-300';
        }
    };

    const PositionBar = ({ occupied, total = 4 }) => {
        return (
            <div className="flex gap-1">
                {Array.from({ length: total }, (_, i) => (
                    <div
                        key={i}
                        className={`h-2 flex-1 rounded-full transition-all ${
                            i < occupied ? 'bg-emerald-500' : 'bg-gray-200'
                        }`}
                    />
                ))}
            </div>
        );
    };

    // ============================================
    // РЕНДЕР
    // ============================================

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center">
                    <Loader className="animate-spin mx-auto mb-3 text-emerald-500" size={32} />
                    <p className="text-gray-500 text-sm">Загрузка Mini Keys...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6">
            {/* Шапка */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className="flex items-center gap-2 sm:gap-3">
                    <button
                        onClick={onBack || (() => navigate('/clusters'))}
                        className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <ChevronsLeft size={20} className="text-gray-500" />
                    </button>
                    <div>
                        <h1 className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
                            Light Keys Mini
                        </h1>
                        <p className="text-xs sm:text-sm text-gray-500">Вход $20 → Выход $35</p>
                    </div>
                </div>
                <button
                    onClick={() => fetchData(false)}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <RefreshCw size={18} className="text-gray-400" />
                </button>
            </div>

            {/* Уведомления */}
            {notification && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                    notification.type === 'success'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                    {notification.message}
                </div>
            )}

            {/* Статус обработки покупки */}
            {isProcessing && purchaseStatus && (
                <div className="mb-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-4 animate-pulse">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center">
                            <Loader className="animate-spin mr-2 text-emerald-500" size={20} />
                            <span className="font-semibold text-emerald-700 text-base">Покупка обрабатывается...</span>
                        </div>
                        <Timer size={18} className="text-emerald-500" />
                    </div>
                    <div className="bg-white/50 rounded-lg p-3 mb-3">
                        <p className="text-sm text-gray-700 mb-2">
                            <span className="font-semibold">Клик зарегистрирован:</span>
                        </p>
                        <div className="flex items-center justify-between">
                            <MicrosecondsDisplay dateString={purchaseStatus.clicked_at} short={false} />
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">
                                {purchaseStatus.status || 'pending'}
                            </span>
                        </div>
                    </div>
                    <div className="text-xs text-gray-600 bg-emerald-50 p-2 rounded flex items-center justify-between">
                        <span>Обработка обычно занимает 1-3 минуты.</span>
                        {processingTime > 0 && (
                            <span className="font-mono font-semibold text-emerald-600">
                                {Math.floor(processingTime / 60)}:{(processingTime % 60).toString().padStart(2, '0')}
                            </span>
                        )}
                    </div>
                    {purchaseStatus.error_message && (
                        <div className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                            {purchaseStatus.error_message}
                        </div>
                    )}
                </div>
            )}

            {/* Статистика */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="bg-emerald-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5">
                        <Users size={14} className="text-emerald-500 mr-1.5" />
                        <span className="text-[10px] sm:text-xs font-medium text-gray-700">В очереди</span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-emerald-500">{statistics.total_in_queue}</p>
                </div>

                <div className="bg-emerald-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5">
                        <User size={14} className="text-emerald-500 mr-1.5" />
                        <span className="text-[10px] sm:text-xs font-medium text-gray-700">Ваши места</span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-emerald-500">{statistics.user_places}</p>
                </div>

                <div className="bg-emerald-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5">
                        <Award size={14} className="text-emerald-500 mr-1.5" />
                        <span className="text-[10px] sm:text-xs font-medium text-gray-700">Заработано</span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-emerald-500">${statistics.earned}</p>
                </div>

                <div className="bg-amber-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5">
                        <AlertCircle size={14} className="text-amber-500 mr-1.5" />
                        <span className="text-[10px] sm:text-xs font-medium text-gray-700">Резервный фонд</span>
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-amber-500">${statistics.reserve_fund}</p>
                </div>

                <div className="bg-emerald-500/5 rounded-xl p-3 sm:p-4">
                    <div className="flex items-center mb-1.5">
                        <Clock size={14} className="text-emerald-500 mr-1.5" />
                        <span className="text-[10px] sm:text-xs font-medium text-gray-700">До выплаты</span>
                    </div>
                    <p className="text-sm sm:text-base font-bold text-emerald-500">{statistics.estimated_time_to_payment}</p>
                </div>
            </div>

            {/* Кнопка покупки + таймер кулдауна */}
            <div className="mb-4 sm:mb-6">
                {purchaseCountdown > 0 && purchaseInfo?.reason !== 'not_started' ? (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                        <p className="text-sm text-gray-600 mb-2">Следующая покупка доступна через:</p>
                        <div className="flex justify-center gap-2">
                            {countdown.days > 0 && (
                                <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                                    <span className="text-xl font-bold text-gray-700">{countdown.days}</span>
                                    <span className="text-xs text-gray-400 ml-1">дн</span>
                                </div>
                            )}
                            <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                                <span className="text-xl font-bold text-gray-700">{countdown.hours}</span>
                                <span className="text-xs text-gray-400 ml-1">ч</span>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                                <span className="text-xl font-bold text-gray-700">{countdown.mins}</span>
                                <span className="text-xs text-gray-400 ml-1">мин</span>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 shadow-sm">
                                <span className="text-xl font-bold text-gray-700">{countdown.secs}</span>
                                <span className="text-xs text-gray-400 ml-1">сек</span>
                            </div>
                        </div>
                    </div>
                ) : purchaseCountdown > 0 && purchaseInfo?.reason === 'not_started' ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                        <p className="text-sm text-emerald-700 mb-2 font-medium">🚀 Старт Mini Keys через:</p>
                        <div className="flex justify-center gap-2">
                            {countdown.days > 0 && (
                                <div className="bg-white rounded-lg px-3 py-2 shadow-sm border border-emerald-100">
                                    <span className="text-xl font-bold text-emerald-600">{countdown.days}</span>
                                    <span className="text-xs text-emerald-400 ml-1">дн</span>
                                </div>
                            )}
                            <div className="bg-white rounded-lg px-3 py-2 shadow-sm border border-emerald-100">
                                <span className="text-xl font-bold text-emerald-600">{countdown.hours}</span>
                                <span className="text-xs text-emerald-400 ml-1">ч</span>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 shadow-sm border border-emerald-100">
                                <span className="text-xl font-bold text-emerald-600">{countdown.mins}</span>
                                <span className="text-xs text-emerald-400 ml-1">мин</span>
                            </div>
                            <div className="bg-white rounded-lg px-3 py-2 shadow-sm border border-emerald-100">
                                <span className="text-xl font-bold text-emerald-600">{countdown.secs}</span>
                                <span className="text-xs text-emerald-400 ml-1">сек</span>
                            </div>
                        </div>

                        {/* Счётчик готовых участников */}
                        {totalReady > 0 && (
                            <div className="mt-3 flex items-center justify-center gap-1.5">
                                <Users size={14} className="text-emerald-600" />
                                <span className="text-sm font-medium text-emerald-700">
                                    {totalReady} {totalReady === 1 ? 'участник готов' : totalReady < 5 ? 'участника готовы' : 'участников готовы'}
                                </span>
                            </div>
                        )}

                        {/* Кнопка "Я готов участвовать" */}
                        <div className="mt-4">
                            {isReady ? (
                                <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-100 text-emerald-700 font-semibold text-sm">
                                    <Award size={16} />
                                    Вы готовы к старту!
                                </div>
                            ) : (
                                <button
                                    onClick={handleMarkReady}
                                    disabled={markingReady}
                                    className={`w-full sm:w-auto px-6 py-3 rounded-xl font-semibold text-white transition-all ${
                                        markingReady
                                            ? 'bg-gray-300 cursor-not-allowed'
                                            : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                                    }`}
                                >
                                    {markingReady ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Loader className="animate-spin" size={16} />
                                            Проверка...
                                        </span>
                                    ) : (
                                        <span className="flex items-center justify-center gap-2">
                                            <Zap size={16} />
                                            Я готов участвовать!
                                        </span>
                                    )}
                                </button>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="flex gap-2">
                        <button
                            onClick={handlePurchase}
                            disabled={purchasing || !canPurchase || isProcessing || purchaseInfo?.reason === 'has_pending_click'}
                            className={`flex-1 py-3 sm:py-4 rounded-xl font-semibold text-white transition-all ${
                                purchasing || !canPurchase || isProcessing || purchaseInfo?.reason === 'has_pending_click'
                                    ? 'bg-gray-300 cursor-not-allowed'
                                    : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                            }`}
                        >
                            {purchasing ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader className="animate-spin" size={18} />
                                    Отправка запроса...
                                </span>
                            ) : isProcessing ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader className="animate-spin" size={18} />
                                    Обработка покупки...
                                </span>
                            ) : !canPurchase ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Clock size={18} />
                                    Покупка недоступна
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <Plus size={18} />
                                    Купить место — $20
                                </span>
                            )}
                        </button>

                        {/* Кнопка ручной проверки статуса */}
                        {isProcessing && (
                            <button
                                onClick={async () => {
                                    setIsCheckingStatus(true);
                                    try {
                                        const response = await axios.get(`${API_BASE}/purchase-status`, {
                                            headers: getAuthHeaders()
                                        });
                                        if (response.data.success) {
                                            const data = response.data.data;
                                            if (!data.has_click || data.status === 'processed') {
                                                setIsProcessing(false);
                                                setPurchaseStatus(null);
                                                setNotification({ type: 'success', message: 'Покупка успешно обработана!' });
                                                fetchData(false);
                                            } else if (data.status === 'failed') {
                                                setIsProcessing(false);
                                                setPurchaseStatus(null);
                                                setNotification({ type: 'error', message: data.error_message || 'Ошибка обработки' });
                                                fetchData(false);
                                            } else {
                                                setPurchaseStatus(data);
                                            }
                                        }
                                    } catch (error) {
                                        console.error('Error checking status:', error);
                                    } finally {
                                        setIsCheckingStatus(false);
                                    }
                                }}
                                disabled={isCheckingStatus}
                                className="py-3 px-4 rounded-xl font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-all flex items-center gap-2 disabled:opacity-50 shadow-md hover:shadow-xl"
                            >
                                {isCheckingStatus ? (
                                    <Loader className="animate-spin" size={16} />
                                ) : (
                                    <RefreshCw size={16} />
                                )}
                                <span className="hidden sm:inline">{isCheckingStatus ? 'Проверка...' : 'Проверить'}</span>
                            </button>
                        )}
                    </div>
                )}

                {/* Подсказки */}
                {canPurchase && !purchasing && !isProcessing && purchaseCountdown === 0 && (
                    <p className="text-center text-[10px] text-gray-500 mt-2">
                        После покупки следующая будет доступна через 24 часа
                    </p>
                )}

                {isProcessing && (
                    <p className="text-center text-xs text-emerald-600 mt-3 font-medium bg-emerald-50 py-2 px-4 rounded-lg border border-emerald-200">
                        Ваша покупка в обработке. Нажмите "Проверить" для обновления статуса.
                    </p>
                )}
            </div>

            {/* Информация о распределении */}
            <div className="mb-4 sm:mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-emerald-700 mb-3">Распределение при закрытии позиций:</h3>
                <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-white/80 rounded-lg p-2.5">
                        <div className="font-medium text-emerald-600 mb-1">Позиция 1</div>
                        <div className="text-gray-600 space-y-0.5">
                            <div>$1 — Фонд комьюнити</div>
                            <div>$2 — Реф. 1 уровня</div>
                            <div>$1 — Реф. 2 уровня</div>
                            <div>$1 — Реф. 3 уровня</div>
                        </div>
                    </div>
                    <div className="bg-white/80 rounded-lg p-2.5">
                        <div className="font-medium text-emerald-600 mb-1">Позиция 2</div>
                        <div className="text-gray-600">$20 — Резервный фонд</div>
                    </div>
                    <div className="bg-white/80 rounded-lg p-2.5">
                        <div className="font-medium text-emerald-600 mb-1">Позиция 3</div>
                        <div className="text-gray-600">$35 — Вознаграждение</div>
                    </div>
                    <div className="bg-white/80 rounded-lg p-2.5">
                        <div className="font-medium text-emerald-600 mb-1">Позиция 4</div>
                        <div className="text-gray-600">$20 — Реинвест</div>
                    </div>
                </div>
            </div>

            {/* ====== ВАШИ НЕЗАКРЫТЫЕ МЕСТА ====== */}
            {(userPlaces.length > 0 || pendingPlaces.length > 0) && (
                <div className="mb-4 sm:mb-6">
                    <h2 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <TrendingUp size={16} className="text-emerald-500" />
                        Ваши незакрытые места
                        <span className="text-xs font-normal text-gray-400">
                            ({userPlaces.length + pendingPlaces.length})
                        </span>
                    </h2>
                    <div className="grid gap-2 sm:gap-3">
                        {/* Места в основной очереди */}
                        {userPlaces.map((place) => (
                            <div key={`main-${place.id}`} className="bg-white border border-emerald-200 rounded-xl p-3 sm:p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                                            Основная
                                        </span>
                                        <span className="text-xs font-medium text-emerald-600">
                                            #{place.queue_position} в очереди
                                        </span>
                                    </div>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        place.positions_to_complete <= 1
                                            ? 'bg-yellow-100 text-yellow-700'
                                            : 'bg-emerald-50 text-emerald-700'
                                    }`}>
                                        {place.status}
                                    </span>
                                </div>
                                <PositionBar occupied={4 - place.positions_to_complete} />
                                <div className="flex justify-between mt-2 text-[10px] sm:text-xs text-gray-500">
                                    <span>Осталось: {place.positions_to_complete} из 4</span>
                                </div>
                                {/* Точное время активации */}
                                <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1.5">
                                    <Clock size={10} className="text-emerald-400" />
                                    <span className="text-[10px] text-gray-400">Активация:</span>
                                    <MicrosecondsDisplay dateString={place.activated_at || place.purchase_timestamp} short />
                                </div>
                            </div>
                        ))}

                        {/* Места в предварительной очереди */}
                        {pendingPlaces.map((place) => (
                            <div key={`pending-${place.id}`} className="bg-white border border-amber-200 rounded-xl p-3 sm:p-4 shadow-sm">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                                            Предварительная
                                        </span>
                                        <span className="text-xs font-medium text-amber-600">
                                            #{place.queue_position} в очереди
                                        </span>
                                    </div>
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">
                                        Ожидает
                                    </span>
                                </div>
                                <div className="text-[10px] sm:text-xs text-gray-500">
                                    Ожидание: ~{place.estimated_wait}
                                </div>
                                {/* Точное время активации */}
                                <div className="mt-2 pt-2 border-t border-gray-100 flex items-center gap-1.5">
                                    <Clock size={10} className="text-amber-400" />
                                    <span className="text-[10px] text-gray-400">Время клика:</span>
                                    <MicrosecondsDisplay dateString={place.activation_time || place.clicked_at || place.created_at} short />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ====== ОСНОВНАЯ ОЧЕРЕДЬ ====== */}
            {(queueData.length > 0 || fullMainQueue.length > 0) && (
                <div className="mb-4 sm:mb-6">
                    <h2 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Zap size={16} className="text-emerald-500" />
                        Основная очередь
                        <span className="text-xs font-normal text-gray-400">
                            ({fullMainQueue.length || queueData.length} мест)
                        </span>
                    </h2>

                    {/* Ближайшие к закрытию (карточки) */}
                    <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 mb-3">
                        {queueData.map((item) => (
                            <div
                                key={item.id}
                                className={`bg-white rounded-xl p-3 sm:p-4 shadow-sm border ${
                                    item.isCurrentUser ? 'border-emerald-400 ring-1 ring-emerald-200' : 'border-gray-100'
                                }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-1.5">
                                        <User size={12} className={item.isCurrentUser ? 'text-emerald-500' : 'text-gray-400'} />
                                        <span className={`text-xs font-medium ${
                                            item.isCurrentUser ? 'text-emerald-600' : 'text-gray-600'
                                        }`}>
                                            {item.login}
                                            {item.isCurrentUser && ' (Вы)'}
                                        </span>
                                    </div>
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                        item.remaining <= 1
                                            ? 'bg-yellow-100 text-yellow-700'
                                            : item.remaining <= 2
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'bg-gray-100 text-gray-600'
                                    }`}>
                                        {item.status_text}
                                    </span>
                                </div>
                                <PositionBar occupied={item.occupied_count} />
                                <div className="flex justify-between mt-1.5 text-[10px] text-gray-400">
                                    <span>{item.occupied_count}/4 заполнено</span>
                                    <span>Осталось: {item.remaining}</span>
                                </div>
                                {/* Время активации */}
                                {(item.activated_at || item.purchase_timestamp) && (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                        <div className="flex items-center gap-1 text-[10px] text-gray-400">
                                            <Clock size={10} />
                                            <span>Активация:</span>
                                        </div>
                                        <MicrosecondsDisplay dateString={item.activated_at || item.purchase_timestamp} short />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Полная основная очередь (таблица) */}
                    {fullMainQueue.length > 0 && (
                        <>
                            <button
                                onClick={() => setShowMainQueue(!showMainQueue)}
                                className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-emerald-600 transition-colors mb-2"
                            >
                                <Grid size={14} />
                                <span>Вся основная очередь ({fullMainQueue.length})</span>
                                {showMainQueue ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>

                            {showMainQueue && (
                                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                                    {/* Заголовок таблицы */}
                                    <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-emerald-50 text-[10px] sm:text-xs font-semibold text-emerald-700 border-b border-emerald-100">
                                        <div className="col-span-1 text-center">#</div>
                                        <div className="col-span-3">Логин</div>
                                        <div className="col-span-2 text-center">Заполн.</div>
                                        <div className="col-span-6 text-right">Время активации</div>
                                    </div>
                                    <div className="max-h-[500px] overflow-y-auto">
                                        {fullMainQueue.map((item, index) => (
                                            <div
                                                key={item.id}
                                                className={`grid grid-cols-12 gap-1 px-3 py-2 text-xs border-b border-gray-50 items-center ${
                                                    item.isCurrentUser
                                                        ? 'bg-emerald-50 font-medium'
                                                        : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                                                }`}
                                            >
                                                <div className="col-span-1 text-center text-gray-400">
                                                    {item.queue_position}
                                                </div>
                                                <div className={`col-span-3 ${item.isCurrentUser ? 'text-emerald-600' : 'text-gray-600'}`}>
                                                    {item.login}
                                                    {item.isCurrentUser && ' ⭐'}
                                                </div>
                                                <div className="col-span-2 text-center">
                                                    <div className="flex gap-0.5 justify-center">
                                                        {Array.from({ length: 4 }, (_, i) => (
                                                            <div
                                                                key={i}
                                                                className={`h-1.5 w-3 rounded-full ${
                                                                    i < (item.occupied_count || (4 - (item.remaining || item.positions_to_complete || 4)))
                                                                        ? 'bg-emerald-500'
                                                                        : 'bg-gray-200'
                                                                }`}
                                                            />
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="col-span-6 text-right">
                                                    <MicrosecondsDisplay
                                                        dateString={item.activated_at || item.purchase_timestamp}
                                                        short
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ====== ПРЕДВАРИТЕЛЬНАЯ ОЧЕРЕДЬ (всегда видна внизу) ====== */}
            {fullPendingQueue.length > 0 && (
                <div className="mb-4">
                    <h2 className="text-sm sm:text-base font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <List size={16} className="text-amber-500" />
                        Предварительная очередь
                        <span className="text-xs font-normal text-gray-400">
                            ({fullPendingQueue.length} чел.)
                        </span>
                    </h2>

                    <div className="bg-white border border-amber-200 rounded-xl overflow-hidden">
                        {/* Заголовок таблицы */}
                        <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-amber-50 text-[10px] sm:text-xs font-semibold text-amber-700 border-b border-amber-100">
                            <div className="col-span-1 text-center">#</div>
                            <div className="col-span-3">Логин</div>
                            <div className="col-span-3 text-center">Ожидание</div>
                            <div className="col-span-5 text-right">Время активации</div>
                        </div>
                        <div className="max-h-[400px] overflow-y-auto">
                            {fullPendingQueue.map((item, index) => (
                                <div
                                    key={item.id}
                                    className={`grid grid-cols-12 gap-1 px-3 py-2 text-xs border-b border-gray-50 items-center ${
                                        item.isCurrentUser
                                            ? 'bg-emerald-50 font-medium'
                                            : index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'
                                    }`}
                                >
                                    <div className="col-span-1 text-center text-gray-400">
                                        {item.queue_position}
                                    </div>
                                    <div className={`col-span-3 ${item.isCurrentUser ? 'text-emerald-600' : 'text-gray-600'}`}>
                                        {item.login}
                                        {item.isCurrentUser && ' ⭐'}
                                    </div>
                                    <div className="col-span-3 text-center">
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">
                                            {item.waiting_time || 'Ожидает'}
                                        </span>
                                    </div>
                                    <div className="col-span-5 text-right">
                                        {(item.activation_time || item.clicked_at) ? (
                                            <MicrosecondsDisplay
                                                dateString={item.activation_time || item.clicked_at}
                                                short
                                            />
                                        ) : (
                                            <span className="text-[10px] text-gray-400">—</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LightMiniKeysCluster;
